import { glow, makeFish, moveFish, drawFish } from "@/vessels";
import { microdot } from "@/microdot";
import { snake, worm } from "@/modes";
import { afx } from "@/afx";
import { mayday } from "@/maydayMode";
import { birdsong } from "@/birdsong";
import { tron } from "@/tron";
import { circuit } from "@/circuit";
import { milkyway } from "@/milkyway";

const rand = (a, b) => a + Math.random() * (b - a);
const TAU = Math.PI * 2;

const fade = (ctx, w, h, alpha, color = "10,10,12") => {
  ctx.globalCompositeOperation = "source-over";
  ctx.fillStyle = `rgba(${color},${alpha})`;
  ctx.fillRect(0, 0, w, h);
};

const eigengrau = {
  id: "eigengrau",
  name: "Eigengrau",
  subtitle: "The intrinsic grey of the dark-adapted eye",
  init(w, h) {
    const c = document.createElement("canvas");
    c.width = Math.ceil(w / 4);
    c.height = Math.ceil(h / 4);
    return { c, cx: c.getContext("2d"), sparks: [] };
  },
  draw(ctx, w, h, t, s, p, k) {
    const { c, cx } = s;
    const img = cx.createImageData(c.width, c.height);
    const d = img.data;
    const breathe = Math.sin(t * 0.4) * 4 + p * 22;
    for (let i = 0; i < d.length; i += 4) {
      const v = 26 + breathe + Math.random() * 22;
      d[i] = v;
      d[i + 1] = v;
      d[i + 2] = v + 3;
      d[i + 3] = 255;
    }
    cx.putImageData(img, 0, 0);
    ctx.globalCompositeOperation = "source-over";
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(c, 0, 0, w, h);
    if (Math.random() < 0.12 + p * 0.5) {
      s.sparks.push({ x: rand(0, w), y: rand(0, h), r: rand(10, 60 + p * 80), life: 1, hue: rand(180, 320) - p * 150 });
    }
    ctx.globalCompositeOperation = "lighter";
    s.sparks = s.sparks.filter((sp) => {
      sp.life -= 0.012 * k;
      glow(ctx, sp.x, sp.y, sp.r, `${sp.hue},${50 + p * 40}%,60%`, sp.life * (0.25 + p * 0.15));
      return sp.life > 0;
    });
  },
};

const phosphenes = {
  id: "phosphenes",
  name: "Phosphenes",
  subtitle: "Drifting clouds of light born in the retina",
  init(w, h) {
    const hues = [280, 200, 140, 40, 330];
    return {
      blobs: Array.from({ length: 9 }, (_, i) => ({
        x: rand(0, w), y: rand(0, h),
        vx: rand(-0.4, 0.4), vy: rand(-0.4, 0.4),
        r: rand(Math.min(w, h) * 0.15, Math.min(w, h) * 0.4),
        hue: hues[i % hues.length], ph: rand(0, TAU),
      })),
    };
  },
  draw(ctx, w, h, t, s, p, k) {
    fade(ctx, w, h, 0.05);
    ctx.globalCompositeOperation = "lighter";
    s.blobs.forEach((b) => {
      b.x += (b.vx + Math.sin(t * 0.3 + b.ph) * 0.6) * k;
      b.y += (b.vy + Math.cos(t * 0.25 + b.ph) * 0.6) * k;
      if (b.x < -b.r) b.x = w + b.r;
      if (b.x > w + b.r) b.x = -b.r;
      if (b.y < -b.r) b.y = h + b.r;
      if (b.y > h + b.r) b.y = -b.r;
      const pulse = 0.5 + 0.5 * Math.sin(t * 0.7 + b.ph);
      glow(ctx, b.x, b.y, b.r * (0.8 + pulse * 0.3), `${(b.hue + t * 6) % 360},${80 + p * 20}%,55%`, 0.05 + pulse * 0.05 + p * 0.06);
    });
  },
};

const kaleidoscope = {
  id: "kaleidoscope",
  name: "Kaleidoscope",
  subtitle: "Form constants from the visual cortex",
  init() {
    return { seg: 12 };
  },
  draw(ctx, w, h, t, s, p) {
    fade(ctx, w, h, 0.12);
    const R = Math.hypot(w, h) / 2;
    s.seg = p > 0.5 ? 16 : 12;
    ctx.save();
    ctx.translate(w / 2, h / 2);
    ctx.globalCompositeOperation = "lighter";
    ctx.lineWidth = 2.5;
    for (let i = 0; i < s.seg; i++) {
      ctx.save();
      ctx.rotate((i * TAU) / s.seg);
      if (i % 2) ctx.scale(1, -1);
      for (let k = 0; k < 7; k++) {
        const d = ((k / 7 + t * 0.04) % 1) * R;
        const hue = (t * 12 + k * 40) % 360;
        const a = (0.6 + p * 0.3) * Math.sin((d / R) * Math.PI);
        ctx.strokeStyle = `hsla(${hue},85%,60%,${a})`;
        ctx.beginPath();
        ctx.moveTo(d, 0);
        ctx.lineTo(d * 0.6, d * Math.tan(Math.PI / s.seg) * 0.6);
        ctx.stroke();
        ctx.globalCompositeOperation = "lighter";
        glow(ctx, d * 0.85, d * 0.12, 6 + (d / R) * 18, `${(hue + 120) % 360},90%,65%`, a * 0.45);
        ctx.globalCompositeOperation = "lighter";
      }
      ctx.restore();
    }
    ctx.restore();
  },
};

const afterglow = {
  id: "afterglow",
  name: "Afterglow",
  subtitle: "Ghost images that linger behind the eyelids",
  init() {
    return {};
  },
  draw(ctx, w, h, t, s, p) {
    fade(ctx, w, h, 0.08);
    ctx.globalCompositeOperation = "lighter";
    const hue = (180 + t * 15) % 360;
    const bw = w * 0.22, bh = h * 0.32;
    const x = w / 2 + Math.sin(t * 0.15) * w * 0.1;
    const y = h / 2 + Math.cos(t * 0.12) * h * 0.06;
    const a = 0.012 + 0.008 * Math.sin(t * 0.5) + p * 0.015;
    for (let i = 0; i < 3; i++) {
      ctx.save();
      ctx.translate(x, y);
      ctx.scale(bw / bh, 1);
      glow(ctx, 0, 0, bh * (0.6 + i * 0.25), `${(hue + i * 40) % 360},70%,55%`, a * 4);
      ctx.restore();
    }
  },
};

const dream = {
  id: "dream",
  name: "yellow blobby fish",
  subtitle: "Just yellow blobby fish, drifting in the dark",
  init(w, h) {
    return { school: Array.from({ length: 9 }, () => ({ ...makeFish(w, h), kind: Math.random() < 0.6 ? "puffer" : "puff" })) };
  },
  draw(ctx, w, h, t, s, p, k, fctx = ctx) {
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "#07060d";
    ctx.fillRect(0, 0, w, h);
    s.school.forEach((f) => {
      moveFish(f, w, h, t, k * 1.2);
      drawFish(fctx, f, t, 0.06 + p * 0.02);
    });
  },
};

export const EFFECTS = [eigengrau, phosphenes, kaleidoscope, afterglow, dream, microdot, snake, worm, afx, birdsong, tron, circuit, milkyway, mayday];
export const ACID_MODES = ["afx", "mayday"];
